import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import PageLayout from '../components/PageLayout';
import { useToast } from '../context/ToastContext';
import ManagementCoupons from './ManagementCoupons';
import { useClientPagination } from '../hooks/useClientPagination';
import TablePagination from '../components/TablePagination';
import TableSearch from '../components/TableSearch';

const ManagementSettings = () => {
    const { success, error: toastError } = useToast();
    const [activeTab, setActiveTab] = useState('plans');
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState({
        siteName: 'CyberGuard Admin',
        supportEmail: 'admin@cyberguard.com',
        plans: [],
        feature_dictionary: [],
        cron_settings: {
            ssl_scan_interval: 300,
            whois_scan_interval: 86400,
            port_scan_interval: 300,
            malware_scan_interval: 3600,
            dos_monitor_interval: 60
        }
    });

    // Plans Editing State
    const [editingPlan, setEditingPlan] = useState(null);
    const [newPlan, setNewPlan] = useState(false);

    // Features Editing State
    const [newFeature, setNewFeature] = useState('');
    const [planSearch, setPlanSearch] = useState('');
    const [featureSearch, setFeatureSearch] = useState('');

    const filteredPlans = useMemo(() => {
        const q = planSearch.trim().toLowerCase();
        const plans = settings.plans || [];
        if (!q) return plans;
        return plans.filter((p) =>
            [p.name, String(p.price), ...(p.features || [])].join(' ').toLowerCase().includes(q)
        );
    }, [settings.plans, planSearch]);
    const plansPage = useClientPagination(filteredPlans, planSearch);

    const filteredFeatures = useMemo(() => {
        const q = featureSearch.trim().toLowerCase();
        const features = settings.feature_dictionary || [];
        if (!q) return features;
        return features.filter((f) => String(f).toLowerCase().includes(q));
    }, [settings.feature_dictionary, featureSearch]);
    const featuresPage = useClientPagination(filteredFeatures, featureSearch);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const res = await api.get('/api/settings');
            if (res.data.success) {
                // Merge with defaults if keys missing
                setSettings(prev => ({ ...prev, ...res.data.data }));
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveSetting = async (key, value) => {
        try {
            await api.post('/api/settings', { key, value });
        } catch (error) {
            console.error('Error saving setting:', error);
            toastError('Failed to save setting');
        }
    };

    // Plan Handlers
    const handleEditPlan = (plan) => {
        setEditingPlan({ ...plan });
        setNewPlan(false);
    };

    const handleCreatePlan = () => {
        setEditingPlan({
            id: `plan_${Date.now()}`,
            name: 'New Plan',
            price: 0,
            currency: 'USD',
            interval: 'monthly',
            features: [],
            highlight: false,
            yearlyDiscount: 0
        });
        setNewPlan(true);
    };

    const handlePlanSave = async () => {
        let updatedPlans;
        if (newPlan) {
            updatedPlans = [...(settings.plans || []), editingPlan];
        } else {
            updatedPlans = settings.plans.map(p => p.id === editingPlan.id ? editingPlan : p);
        }

        setSettings(prev => ({ ...prev, plans: updatedPlans }));
        await handleSaveSetting('plans', updatedPlans);
        setEditingPlan(null);
        success('Plan saved successfully!');
    };

    const handlePlanDelete = async (planId) => {
        if (!window.confirm("Are you sure you want to delete this plan?")) return;

        const updatedPlans = settings.plans.filter(p => p.id !== planId);
        setSettings(prev => ({ ...prev, plans: updatedPlans }));
        await handleSaveSetting('plans', updatedPlans);
    };

    // Feature Dictionary Handlers
    const handleAddGlobalFeature = async () => {
        if (!newFeature.trim()) return;
        const updatedFeatures = [...(settings.feature_dictionary || []), newFeature.trim()];
        setSettings(prev => ({ ...prev, feature_dictionary: updatedFeatures }));
        await handleSaveSetting('feature_dictionary', updatedFeatures);
        setNewFeature('');
    };

    const handleDeleteGlobalFeature = async (feature) => {
        if (!window.confirm(`Delete feature "${feature}"? This will NOT remove it from existing plans.`)) return;
        const updatedFeatures = settings.feature_dictionary.filter(f => f !== feature);
        setSettings(prev => ({ ...prev, feature_dictionary: updatedFeatures }));
        await handleSaveSetting('feature_dictionary', updatedFeatures);
    };

    const togglePlanFeature = (feature) => {
        let updatedFeatures;
        if (editingPlan.features.includes(feature)) {
            updatedFeatures = editingPlan.features.filter(f => f !== feature);
        } else {
            updatedFeatures = [...editingPlan.features, feature];
        }
        setEditingPlan({ ...editingPlan, features: updatedFeatures });
    };

    if (loading) {
        return (
            <PageLayout title="System Configuration">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout title="System Configuration">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="h4 mb-1">
                        <i className="las la-sliders-h me-2 text-warning"></i>
                        System Configuration
                    </h2>
                    <p className="text-muted mb-0">Manage global settings, pricing plans, and system parameters.</p>
                </div>
                <Link to="/management/dashboard" className="btn btn-outline-secondary">
                    <i className="las la-arrow-left me-2"></i> Dashboard
                </Link>
            </div>

            <div className="row">
                {/* Sidebar */}
                <div className="col-md-3 mb-4">
                    <div className="list-group shadow-sm">
                        <button
                            className={`list-group-item list-group-item-action ${activeTab === 'general' ? 'active' : ''}`}
                            onClick={() => setActiveTab('general')}
                        >
                            <i className="las la-cog me-2"></i> General Settings
                        </button>
                        <button
                            className={`list-group-item list-group-item-action ${activeTab === 'features' ? 'active' : ''}`}
                            onClick={() => setActiveTab('features')}
                        >
                            <i className="las la-list me-2"></i> Global Features
                        </button>
                        <button
                            className={`list-group-item list-group-item-action ${activeTab === 'plans' ? 'active' : ''}`}
                            onClick={() => setActiveTab('plans')}
                        >
                            <i className="las la-tags me-2"></i> Plans & Pricing
                        </button>
                        <button
                            className={`list-group-item list-group-item-action ${activeTab === 'coupons' ? 'active' : ''}`}
                            onClick={() => setActiveTab('coupons')}
                        >
                            <i className="las la-ticket-alt me-2"></i> Coupons
                        </button>
                        <button
                            className={`list-group-item list-group-item-action ${activeTab === 'security' ? 'active' : ''}`}
                            onClick={() => setActiveTab('security')}
                        >
                            <i className="las la-shield-alt me-2"></i> Security
                        </button>
                        <button
                            className={`list-group-item list-group-item-action ${activeTab === 'cron' ? 'active' : ''}`}
                            onClick={() => setActiveTab('cron')}
                        >
                            <i className="las la-clock me-2"></i> Cron Schedules
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="col-md-9">
                    <div className="card shadow-sm border-0">
                        <div className="card-body p-4">
                            {activeTab === 'general' && (
                                <div>
                                    <h5 className="card-title mb-4">General Settings</h5>
                                    <div className="mb-3">
                                        <label className="form-label">Site Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={settings.siteName || ''}
                                            onChange={(e) => setSettings({ ...settings, siteName: e.target.value })}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Support Email</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            value={settings.supportEmail || ''}
                                            onChange={(e) => setSettings({ ...settings, supportEmail: e.target.value })}
                                        />
                                    </div>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => {
                                            handleSaveSetting('siteName', settings.siteName);
                                            handleSaveSetting('supportEmail', settings.supportEmail);
                                            success('Settings saved!');
                                        }}
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            )}

                            {activeTab === 'features' && (
                                <div>
                                    <h5 className="card-title mb-4">Global Feature Library</h5>
                                    <p className="text-muted small mb-4">Define features here that can be enabled in Subsription Plans.</p>

                                    <div className="input-group mb-3">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Add new feature (e.g. 'Advanced Analytics')"
                                            value={newFeature}
                                            onChange={(e) => setNewFeature(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddGlobalFeature()}
                                        />
                                        <button className="btn btn-success" onClick={handleAddGlobalFeature}>Add Feature</button>
                                    </div>

                                    <div className="mb-3">
                                        <TableSearch
                                            value={featureSearch}
                                            onChange={setFeatureSearch}
                                            placeholder="Search features…"
                                        />
                                    </div>

                                    <ul className="list-group">
                                        {featuresPage.totalCount > 0 ? (
                                            featuresPage.items.map((feature, idx) => (
                                                <li key={`${feature}-${idx}`} className="list-group-item d-flex justify-content-between align-items-center">
                                                    {feature}
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleDeleteGlobalFeature(feature)}
                                                    >
                                                        <i className="las la-trash"></i>
                                                    </button>
                                                </li>
                                            ))
                                        ) : (
                                            <li className="list-group-item text-muted text-center">No global features defined.</li>
                                        )}
                                    </ul>
                                    <TablePagination
                                        currentPage={featuresPage.currentPage}
                                        totalPages={featuresPage.totalPages}
                                        totalCount={featuresPage.totalCount}
                                        showingFrom={featuresPage.showingFrom}
                                        showingTo={featuresPage.showingTo}
                                        onPageChange={featuresPage.setCurrentPage}
                                    />
                                </div>
                            )}

                            {activeTab === 'plans' && (
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <h5 className="card-title mb-0">Subscription Plans</h5>
                                        <button className="btn btn-sm btn-success" onClick={handleCreatePlan}>
                                            <i className="las la-plus me-1"></i> Add New Plan
                                        </button>
                                    </div>

                                    {editingPlan ? (
                                        <div className="bg-light p-4 rounded border">
                                            <h6 className="mb-3">{newPlan ? 'Create Plan' : 'Edit Plan'}</h6>
                                            <div className="row g-3">
                                                <div className="col-md-6">
                                                    <label className="form-label">Plan Name</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={editingPlan.name}
                                                        onChange={(e) => setEditingPlan({ ...editingPlan, name: e.target.value })}
                                                    />
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label">Price (USD)</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={editingPlan.price}
                                                        onChange={(e) => setEditingPlan({ ...editingPlan, price: Number(e.target.value) })}
                                                    />
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label">Yearly Discount (%)</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={editingPlan.yearlyDiscount}
                                                        onChange={(e) => setEditingPlan({ ...editingPlan, yearlyDiscount: Number(e.target.value) })}
                                                    />
                                                </div>

                                                <div className="col-12">
                                                    <label className="form-label fw-bold">Features (Select to Disable/Enable)</label>
                                                    <div className="card p-3">
                                                        <div className="row">
                                                            {settings.feature_dictionary && settings.feature_dictionary.map((feature, idx) => (
                                                                <div className="col-md-6 mb-2" key={idx}>
                                                                    <div className="form-check">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            id={`feature-${idx}`}
                                                                            checked={editingPlan.features.includes(feature)}
                                                                            onChange={() => togglePlanFeature(feature)}
                                                                        />
                                                                        <label className="form-check-label" htmlFor={`feature-${idx}`}>
                                                                            {feature}
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <small className="text-muted mt-2 d-block">Manage the available features list in the "Global Features" tab.</small>
                                                </div>

                                                <div className="col-12 mt-4">
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={editingPlan.highlight}
                                                            onChange={(e) => setEditingPlan({ ...editingPlan, highlight: e.target.checked })}
                                                            id="highlightPlan"
                                                        />
                                                        <label className="form-check-label" htmlFor="highlightPlan">
                                                            Highlight this plan (e.g. "Most Popular")
                                                        </label>
                                                    </div>
                                                </div>

                                                <div className="col-12 mt-4 d-flex gap-2">
                                                    <button className="btn btn-primary" onClick={handlePlanSave}>
                                                        {newPlan ? 'Create Plan' : 'Update Plan'}
                                                    </button>
                                                    <button className="btn btn-secondary" onClick={() => setEditingPlan(null)}>
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                        <div className="mb-3">
                                            <TableSearch
                                                value={planSearch}
                                                onChange={setPlanSearch}
                                                placeholder="Search plans by name or feature…"
                                            />
                                        </div>
                                        <div className="table-responsive">
                                            <table className="table table-hover align-middle">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Name</th>
                                                        <th>Price</th>
                                                        <th>Features Count</th>
                                                        <th>Status</th>
                                                        <th className="text-end">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {plansPage.totalCount > 0 ? (
                                                        plansPage.items.map(plan => (
                                                            <tr key={plan.id}>
                                                                <td>
                                                                    <strong>{plan.name}</strong>
                                                                    {plan.highlight && <span className="badge bg-warning text-dark ms-2">Highlighted</span>}
                                                                </td>
                                                                <td>${plan.price} / mo</td>
                                                                <td>
                                                                    <span className="badge bg-secondary">{plan.features.length} features</span>
                                                                    {plan.features.includes("MFA / 2FA Security") && (
                                                                        <span className="badge bg-info ms-1"><i className="las la-lock"></i> MFA</span>
                                                                    )}
                                                                </td>
                                                                <td><span className="badge bg-success">Active</span></td>
                                                                <td className="text-end">
                                                                    <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEditPlan(plan)}>
                                                                        <i className="las la-edit"></i>
                                                                    </button>
                                                                    <button className="btn btn-sm btn-outline-danger" onClick={() => handlePlanDelete(plan.id)}>
                                                                        <i className="las la-trash"></i>
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="5" className="text-center py-4 text-muted">No plans configured.</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        <TablePagination
                                            currentPage={plansPage.currentPage}
                                            totalPages={plansPage.totalPages}
                                            totalCount={plansPage.totalCount}
                                            showingFrom={plansPage.showingFrom}
                                            showingTo={plansPage.showingTo}
                                            onPageChange={plansPage.setCurrentPage}
                                        />
                                        </>
                                    )}
                                </div>
                            )}

                            {activeTab === 'coupons' && (
                                <ManagementCoupons embedded={true} />
                            )}

                            {activeTab === 'security' && (
                                <div>
                                    <h5 className="card-title mb-4">Security Settings</h5>
                                    <div className="alert alert-info">
                                        <i className="las la-info-circle me-2"></i>
                                        Configure global security parameters for the platform.
                                    </div>
                                    {/* Placeholders for future security settings */}
                                    <div className="form-check form-switch mb-3">
                                        <input className="form-check-input" type="checkbox" id="mfa" defaultChecked />
                                        <label className="form-check-label" htmlFor="mfa">Enforce MFA for Admins</label>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'cron' && (
                                <div>
                                    <h5 className="card-title mb-4">Cron Schedules</h5>
                                    <p className="text-muted small mb-4">Set the minimum interval (in seconds) for each type of background scan. Smaller values increase server load.</p>

                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label">SSL Scan Interval (s)</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={settings.cron_settings?.ssl_scan_interval || 300}
                                                onChange={(e) => setSettings({
                                                    ...settings,
                                                    cron_settings: { ...settings.cron_settings, ssl_scan_interval: Number(e.target.value) }
                                                })}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">WHOIS Scan Interval (s)</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={settings.cron_settings?.whois_scan_interval || 86400}
                                                onChange={(e) => setSettings({
                                                    ...settings,
                                                    cron_settings: { ...settings.cron_settings, whois_scan_interval: Number(e.target.value) }
                                                })}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Port Scan Interval (s)</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={settings.cron_settings?.port_scan_interval || 300}
                                                onChange={(e) => setSettings({
                                                    ...settings,
                                                    cron_settings: { ...settings.cron_settings, port_scan_interval: Number(e.target.value) }
                                                })}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Malware Scan Interval (s)</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={settings.cron_settings?.malware_scan_interval || 3600}
                                                onChange={(e) => setSettings({
                                                    ...settings,
                                                    cron_settings: { ...settings.cron_settings, malware_scan_interval: Number(e.target.value) }
                                                })}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Proxied Scan Data Log Interval (s)</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={settings.cron_settings?.dos_monitor_interval || 60}
                                                onChange={(e) => setSettings({
                                                    ...settings,
                                                    cron_settings: { ...settings.cron_settings, dos_monitor_interval: Number(e.target.value) }
                                                })}
                                            />
                                        </div>
                                    </div>

                                    <button
                                        className="btn btn-primary mt-4"
                                        onClick={() => {
                                            handleSaveSetting('cron_settings', settings.cron_settings);
                                            success('Cron settings saved!');
                                        }}
                                    >
                                        Save Cron Settings
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </PageLayout>
    );
};

export default ManagementSettings;
